import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import StatusPill from '../../components/shared/StatusPill';
import TrendChip from '../../components/shared/TrendChip';
import SectionDivider from '../../components/shared/SectionDivider';
import { statesData, gujaratDISCOMs, nationalKPIs } from '../../data/mockData';

export default function NationalStates() {
  const [expandedState, setExpandedState] = useState<string | null>(null);

  const handleRowClick = (stateId: string) => {
    setExpandedState(prev => (prev === stateId ? null : stateId));
  };

  const getPRColor = (pr: number) => {
    if (pr >= 75) return 'text-success font-semibold';
    if (pr >= 68) return 'text-warning font-semibold';
    return 'text-danger font-semibold';
  };

  return (
    <div className="p-6 space-y-6">
      {/* KPIs */}
      <div className="flex gap-4">
        <KPICard title="States Active" value="28" subtext="All monitored" />
        <KPICard title="DISCOMs Onboarded" value="64" subtext="Across India" />
        <KPICard title="Districts Covered" value="312" subtext="Of 766 total" />
        <KPICard title="Feeders Monitored" value="4,820" subtext="Smart-meter enabled" />
        <KPICard title="Avg National PR" value={`${nationalKPIs.avgPR}%`} subtext="Good zone" subtextColor="green" />
      </div>

      {/* Performance matrix */}
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <SectionDivider title="State Performance Matrix" subtitle="Click a row to see DISCOM breakdown" />
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2.5 text-text-tertiary font-medium">State</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">Plants</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">GWp</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">GWh Today</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">PR%</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">CUF%</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">Active%</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">Alarms</th>
                <th className="text-center py-2.5 text-text-tertiary font-medium">MoM</th>
              </tr>
            </thead>
            <tbody>
              {statesData.map(state => (
                <React.Fragment key={state.id}>
                  <tr
                    className={`border-b border-border/50 cursor-pointer hover:bg-bg-secondary transition-colors ${expandedState === state.id ? 'bg-bg-tertiary' : ''}`}
                    onClick={() => handleRowClick(state.id)}
                  >
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        {expandedState === state.id
                          ? <ChevronUp size={12} className="text-accent flex-shrink-0" />
                          : <ChevronDown size={12} className="text-text-tertiary flex-shrink-0" />}
                        <span className="font-medium text-accent hover:underline">{state.name}</span>
                      </div>
                    </td>
                    <td className="py-2.5 text-right text-text-secondary">{state.plants.toLocaleString()}</td>
                    <td className="py-2.5 text-right text-text-secondary">{state.capacityGWp.toFixed(1)}</td>
                    <td className="py-2.5 text-right font-medium text-text-primary">{state.generationGWh.toFixed(2)}</td>
                    <td className={`py-2.5 text-right ${getPRColor(state.prPercent)}`}>{state.prPercent.toFixed(1)}%</td>
                    <td className="py-2.5 text-right text-text-secondary">{state.cufPercent.toFixed(1)}%</td>
                    <td className="py-2.5 text-right text-text-secondary">{state.activePercent.toFixed(0)}%</td>
                    <td className={`py-2.5 text-right ${state.alarms > 200 ? 'text-danger font-semibold' : 'text-text-secondary'}`}>{state.alarms}</td>
                    <td className="py-2.5 text-center">
                      <TrendChip value={state.momTrend} />
                    </td>
                  </tr>

                  {/* DISCOM drill-down panel */}
                  {expandedState === state.id && (
                    <tr>
                      <td colSpan={9} className="p-0">
                        <div className="bg-bg-secondary border-b border-border px-6 py-4">
                          <div className="flex items-center gap-2 mb-3">
                            <button
                              onClick={() => setExpandedState(null)}
                              className="flex items-center gap-1 text-xs text-accent hover:underline"
                            >
                              <ArrowLeft size={11} /> Back
                            </button>
                            <span className="text-xs text-text-secondary">DISCOM breakdown for {state.name}</span>
                          </div>
                          <table className="w-full text-xs bg-white rounded-card overflow-hidden border border-border">
                            <thead>
                              <tr className="bg-bg-tertiary border-b border-border">
                                <th className="text-left py-2 px-3 text-text-tertiary font-medium">DISCOM</th>
                                <th className="text-right py-2 px-3 text-text-tertiary font-medium">Plants</th>
                                <th className="text-right py-2 px-3 text-text-tertiary font-medium">GWh Today</th>
                                <th className="text-right py-2 px-3 text-text-tertiary font-medium">PR%</th>
                                <th className="text-right py-2 px-3 text-text-tertiary font-medium">Active%</th>
                                <th className="text-right py-2 px-3 text-text-tertiary font-medium">Alarms</th>
                              </tr>
                            </thead>
                            <tbody>
                              {gujaratDISCOMs.map(d => (
                                <tr key={d.id} className="border-b border-border/50 hover:bg-bg-secondary cursor-pointer">
                                  <td className="py-2 px-3 font-medium text-text-primary">{d.name}</td>
                                  <td className="py-2 px-3 text-right text-text-secondary">{d.plants.toLocaleString()}</td>
                                  <td className="py-2 px-3 text-right font-medium">{d.generationGWh.toFixed(2)}</td>
                                  <td className={`py-2 px-3 text-right ${getPRColor(d.prPercent)}`}>{d.prPercent.toFixed(1)}%</td>
                                  <td className="py-2 px-3 text-right text-text-secondary">{d.activePercent.toFixed(0)}%</td>
                                  <td className="py-2 px-3 text-right text-text-secondary">{d.alarms}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
